import { memo, useCallback } from 'react'
import { replace } from 'connected-react-router'

import { Paper, Skeleton } from '@mui/material'

import { Button, PageHeader, PageLayout, Tooltip } from '@collaborate-health/uikit'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'

import Routes from '@tabeeb/routes'
import { useDispatch } from '@tabeeb/store'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { useContentTrainingMaterial, useTrainingMaterial } from '@tabeeb/modules/articles/hooks'
import { ArticleContent, ArticleContentSkeleton } from '@tabeeb/modules/articles/components'

export interface ContentTrainingMaterialViewPageProps {
  id: number
}

const ContentTrainingMaterialViewPage: React.FC<ContentTrainingMaterialViewPageProps> = ({ id }) => {
  const dispatch = useDispatch()

  const onError = useCallback(() => {
    dispatch(
      onAddErrorNotification({ message: trainingMaterialTitleFormatter.format('Failed to load training material') })
    )

    dispatch(replace(Routes.trainingMaterials))
  }, [dispatch])

  const {
    loading: contentTrainingMaterialLoading,
    contentTrainingMaterial,
    onMarkAsRead,
    isSubmitInProgress,
  } = useContentTrainingMaterial({ id, onError })

  const onMarkedAsRead = useCallback(() => {
    dispatch(
      onAddSuccessNotification({ message: trainingMaterialTitleFormatter.format('Training material marked as read') })
    )
  }, [dispatch])

  const { loading: trainingMaterialLoading, trainingMaterial } = useTrainingMaterial({
    id: contentTrainingMaterial?.TrainingMaterial?.Id,
    onMarkedAsRead,
    onError,
  })

  return (
    <PageLayout component={Paper} spacing='page' background='default' maxWidth='lg'>
      <PageHeader
        title={trainingMaterial?.Name || <Skeleton width={350} />}
        actions={
          !contentTrainingMaterial?.Read &&
          contentTrainingMaterial?.Assigned && (
            <Tooltip text='Mark as read'>
              <span>
                <Button
                  loading={isSubmitInProgress}
                  disabled={contentTrainingMaterialLoading || trainingMaterialLoading}
                  color='primary'
                  size='small'
                  variant='contained'
                  onClick={() => onMarkAsRead()}
                >
                  Mark as read
                </Button>
              </span>
            </Tooltip>
          )
        }
      />

      {!trainingMaterial?.Content ? <ArticleContentSkeleton /> : <ArticleContent content={trainingMaterial?.Content} />}
    </PageLayout>
  )
}

export default memo(ContentTrainingMaterialViewPage)

